/**
 * Header for the right-pane path detail — name + weight + active switch +
 * remove button. Extracted from BinomPathDetail to keep that file under the
 * per-file size budget.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/layout/Button';
import { Input } from '@/components/ui/form/input/Input';
import { Switch } from '@/components/ui/form/controls/Switch';
import { cn } from '@/lib/utils';
import WeightInput from '@/features/flow-optimizer/tabs/standard/v2/paths/WeightInput';

import type { BinomDraftPath } from '../../../binom-flow-draft.types';

export interface BinomPathHeaderProps {
  path: BinomDraftPath;
  onPatch: (mutator: (p: BinomDraftPath) => void) => void;
  onRemove?: () => void;
}

const BinomPathHeader: FC<BinomPathHeaderProps> = ({
  path,
  onPatch,
  onRemove,
}) => (
  <div className="space-y-1.5">
    <div className="flex items-center justify-between">
      <label className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        Path name
      </label>
      {onRemove && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-destructive hover:bg-destructive/10"
          onClick={() => {
            if (window.confirm(`Remove path "${path.name || path.id}"?`)) onRemove();
          }}
          aria-label="Remove path"
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden />
        </Button>
      )}
    </div>
    <Input
      value={path.name}
      onChange={(e) => onPatch((p) => (p.name = e.target.value))}
      placeholder="Path name"
      aria-label="Path name"
      className="h-8 font-semibold"
    />
    <div className={cn('flex flex-wrap items-center gap-2 rounded border border-border/60 bg-muted/10 px-2 py-1')}>
      <label className="flex items-center gap-1 text-[11px]">
        <span className="font-semibold uppercase tracking-wide text-muted-foreground">
          Weight
        </span>
        <WeightInput
          value={path.weight}
          onChange={(v) => onPatch((p) => (p.weight = v))}
          ariaLabel={`Weight ${path.name || path.id}`}
        />
      </label>
      <div className="flex items-center gap-1.5">
        <Switch
          checked={path.active}
          onCheckedChange={(v) => onPatch((p) => (p.active = v))}
          aria-label={`Path active ${path.name || path.id}`}
        />
        <span
          className={cn(
            'text-[10px] font-bold uppercase',
            path.active ? 'text-success' : 'text-muted-foreground',
          )}
        >
          {path.active ? 'Active' : 'Off'}
        </span>
      </div>
    </div>
  </div>
);

export default BinomPathHeader;
